import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Textarea from '../components/ui/Textarea';
import Label from '../components/ui/Label';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import Modal from '../components/ui/Modal';
import { toast } from 'react-hot-toast';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { api, redirectToLoginIfUnauthorized } from '../services/authService';
import { FASTCUBE_ORG, organizationsService } from '../services/organizationsService';
import { fetchCookieConfig, getGenieSpaces } from '../services/cookieConfigService';
import { getCookie, setCookie } from '../services/cookieService';

const GENIE_CONVERSATION_STORAGE_KEY = 'current_conversation_id';
/** Conversation transcript markdown (tab-scoped; avoids cookie size limits). */
const TRANSCRIPT_STORAGE_KEY = 'current_conversation_transcript';
/** Last Genie API JSON (tab-scoped; avoids ~4KB cookie limits). */
const RAW_RESPONSE_STORAGE_KEY = 'current_conversation_response';
/** Genie space key (cross-tab; remembered across sessions). */
const SELECTED_SPACE_STORAGE_KEY = 'chat_selected_space';
/** Per-turn metadata for feedback (aligned with transcript turns). Tab-scoped JSON in sessionStorage. */
const TRANSCRIPT_TURNS_META_KEY = 'current_conversation_turn_meta';
/** Between Q/A pairs in stored transcript (split for one panel per turn). */
const TRANSCRIPT_TURN_DELIMITER = '\n\n---\n\n';

/** Read turn metadata array from sessionStorage. */
function readTurnMetaFromStorage() {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(TRANSCRIPT_TURNS_META_KEY);
    if (raw == null || !String(raw).trim()) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((m) => ({
      genieQuestionId: m.genieQuestionId != null ? Number(m.genieQuestionId) : null,
      assistantMessageId:
        m.assistantMessageId != null ? String(m.assistantMessageId).trim() || null : null,
      rating: m.rating != null ? Number(m.rating) : null,
      statementId: m.statementId != null ? String(m.statementId).trim() || null : null,
      vizAttachmentIds: Array.isArray(m.vizAttachmentIds)
        ? m.vizAttachmentIds.map((id) => String(id).trim()).filter(Boolean)
        : [],
      conversationId:
        m.conversationId != null ? String(m.conversationId).trim() || null : null,
    }));
  } catch {
    return [];
  }
}

function buildTurnMetaFromQuestions(questions) {
  return (questions || []).map((q) => ({
    genieQuestionId: q.id != null ? Number(q.id) : null,
    assistantMessageId:
      q.assistant_message_id != null ? String(q.assistant_message_id).trim() || null : null,
    rating: q.rating != null ? Number(q.rating) : null,
    statementId: q.statement_id != null ? String(q.statement_id).trim() || null : null,
  }));
}

/** Read previously chosen Genie space key from localStorage. */
function readSelectedSpaceFromStorage() {
  if (typeof localStorage === 'undefined') return '';
  try {
    const v = localStorage.getItem(SELECTED_SPACE_STORAGE_KEY);
    return v != null ? String(v).trim() : '';
  } catch {
    return '';
  }
}

/** Persist chosen Genie space key to localStorage. */
function writeSelectedSpaceToStorage(key) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SELECTED_SPACE_STORAGE_KEY, String(key));
    }
  } catch {
    /* ignore */
  }
}

/** Genie thread id for follow-up API calls (tab-scoped). */
function readConversationIdFromStorage() {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    const id = sessionStorage.getItem(GENIE_CONVERSATION_STORAGE_KEY);
    if (id != null && String(id).trim() !== '') return String(id).trim();
    return '';
  } catch {
    return '';
  }
}

function persistConversationIdFromResponse(data) {
  const cid = data?.conversation?.id;
  if (cid == null) return;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(GENIE_CONVERSATION_STORAGE_KEY, String(cid));
    }
  } catch {
    /* ignore */
  }
}

/** Remove Genie thread id from sessionStorage. */
function clearConversationIdStorage() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(GENIE_CONVERSATION_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Read transcript from sessionStorage. */
function readTranscriptFromStorage() {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    const t = sessionStorage.getItem(TRANSCRIPT_STORAGE_KEY);
    if (t != null && String(t).trim() !== '') return t;
    return '';
  } catch {
    return '';
  }
}

/** Remove transcript from sessionStorage. */
function clearTranscriptStorage() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(TRANSCRIPT_STORAGE_KEY);
      sessionStorage.removeItem(TRANSCRIPT_TURNS_META_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Remove last raw Genie response from sessionStorage. */
function clearRawResponseStorage() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(RAW_RESPONSE_STORAGE_KEY);
    }
  } catch {
    /* quota or private mode */
  }
}

function formatErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail.trim() ? detail : '';
  try {
    return JSON.stringify(detail, null, 2);
  } catch {
    return String(detail);
  }
}

/** Best-effort Genie failure text for send-message catch (FastAPI body). */
function formatChatGenieSendError(err) {
  if (!err) return 'Failed to send message.';
  const candidates = [];
  if (typeof err.genieFailureDetail === 'string' && err.genieFailureDetail.trim()) {
    candidates.push(err.genieFailureDetail.trim());
  }
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    candidates.push(genieFailureDetailFromData(data));
    if (data.detail != null) candidates.push(formatErrorDetail(data.detail));
    candidates.push(formatGenieStreamErrorValue(data.error));
  }
  candidates.push(formatGenieStreamErrorValue(err.message));
  return pickGenieFailureDisplayText(...candidates) || 'Failed to send message.';
}

function formatGenieHistoryDate(iso) {
  if (iso == null || String(iso).trim() === '') return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

/** Friendly Genie space label from ``genie_spaces`` config, or the raw key, or em dash. */
function genieSpaceLabelFromKey(genieSpaces, spaceKey) {
  if (spaceKey == null || String(spaceKey).trim() === '') return '—';
  const k = String(spaceKey).trim();
  const found = genieSpaces.find((s) => s.key === k);
  if (found?.label) return found.label;
  return k;
}

/** Genie may return legacy `content` as a string or an array of string fragments. */
function attachmentContentToString(content) {
  if (content == null) return '';
  if (Array.isArray(content)) {
    return content.map((c) => (c == null ? '' : String(c))).join('');
  }
  return String(content);
}

/** Genie often puts a generic permissions line in attachments; prefer `message.error` when present. */
const GENIE_GENERIC_FAILURE_RE =
  /may not have access to the data tables|verify your table permissions/i;

function isGenericGenieFailureText(text) {
  return typeof text === 'string' && GENIE_GENERIC_FAILURE_RE.test(text);
}

/** Prefer technical errors (SQL_EXECUTION_EXCEPTION, etc.) over generic attachment copy. */
function pickGenieFailureDisplayText(...candidates) {
  const cleaned = candidates
    .map((c) => (typeof c === 'string' ? c.trim() : ''))
    .filter(Boolean);
  const technical = cleaned.find((t) => !isGenericGenieFailureText(t));
  return technical || cleaned[0] || '';
}

/** Walk API payload for SQL / Genie technical errors (when ``message.error`` is omitted). */
function deepFindGenieTechnicalError(value, depth = 0, seen = new Set()) {
  if (value == null || depth > 14) return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s || isGenericGenieFailureText(s)) return '';
    if (
      s.includes('SQL_EXECUTION_EXCEPTION') ||
      s.includes('UNRESOLVED_COLUMN') ||
      s.includes('SQLSTATE:') ||
      /\[UNRESOLVED_[A-Z_.]+\]/.test(s)
    ) {
      return s;
    }
    return '';
  }
  if (typeof value !== 'object') return '';
  if (seen.has(value)) return '';
  seen.add(value);

  if (!Array.isArray(value)) {
    const shaped = extractGenieErrorText(value);
    if (shaped && !isGenericGenieFailureText(shaped)) return shaped;
    if (
      value.type === 'SQL_EXECUTION_EXCEPTION' &&
      typeof value.error === 'string' &&
      value.error.trim()
    ) {
      return `SQL_EXECUTION_EXCEPTION: ${value.error.trim()}`;
    }
  }

  const items = Array.isArray(value) ? value : Object.values(value);
  for (const item of items) {
    const found = deepFindGenieTechnicalError(item, depth + 1, seen);
    if (found) return found;
  }
  return '';
}

/** Technical failure text from a Genie chat API payload. */
function genieFailureDetailFromData(data) {
  if (!data || typeof data !== 'object') return '';
  const explicit = data.genie_failure_detail;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  const fromMessage = extractGenieErrorText(data.message);
  if (fromMessage && !isGenericGenieFailureText(fromMessage)) return fromMessage;
  const deep = deepFindGenieTechnicalError(data);
  if (deep) return deep;
  return fromMessage;
}

async function fetchGenieFailureDetailFromApi({ space, conversationId, messageId, signal }) {
  if (!space || !conversationId || !messageId) return '';
  const params = new URLSearchParams({ space: String(space).trim() });
  const res = await api.get(
    `/api/v2/chat/genie/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/failure-detail?${params}`,
    { signal }
  );
  const detail = res.data?.genie_failure_detail;
  return typeof detail === 'string' ? detail.trim() : '';
}

/** Resolve SQL error text; re-fetch from Databricks when attachment text is generic. */
async function resolveGenieFailureDetail(data, { space, signal }) {
  const preliminary = genieFailureDetailFromData(data);
  if (preliminary && !isGenericGenieFailureText(preliminary)) return preliminary;

  const msg = data?.message;
  const conversationId =
    data?.conversation?.id || msg?.conversation_id || msg?.conversationId;
  const messageId = msg?.id || msg?.message_id || msg?.messageId;
  if (!conversationId || !messageId || !space) return preliminary;

  try {
    const fetched = await fetchGenieFailureDetailFromApi({
      space,
      conversationId: String(conversationId),
      messageId: String(messageId),
      signal,
    });
    if (fetched && !isGenericGenieFailureText(fetched)) return fetched;
    if (fetched) return fetched;
  } catch (e) {
    console.warn('Genie failure-detail fetch:', e);
  }
  return preliminary;
}

/**
 * Human-readable Genie failure text from API shapes:
 * `message.error` string, or `{ error, type }` (e.g. SQL_EXECUTION_EXCEPTION).
 */
function extractGenieErrorText(message) {
  if (!message || typeof message !== 'object') return '';
  const errField = message.error;
  if (typeof errField === 'string' && errField.trim()) {
    const s = errField.trim();
    if (s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s);
        const fromParsed = extractGenieErrorText({ error: parsed });
        if (fromParsed) return fromParsed;
      } catch {
        /* plain string */
      }
    }
    return s;
  }
  if (errField && typeof errField === 'object') {
    const nested =
      (typeof errField.error === 'string' && errField.error.trim()) ||
      (typeof errField.message === 'string' && errField.message.trim()) ||
      (typeof errField.detail === 'string' && errField.detail.trim()) ||
      '';
    const errType =
      (typeof errField.type === 'string' && errField.type.trim()) ||
      (typeof errField.error_type === 'string' && errField.error_type.trim()) ||
      '';
    if (nested) return errType ? `${errType}: ${nested}` : nested;
    try {
      return JSON.stringify(errField);
    } catch {
      return '';
    }
  }
  for (const key of ['error_message', 'message']) {
    const v = message[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Reply body; on FAILED prefer `message.error` over generic attachment text. */
function genieReplyBodyTextFromMessage(message) {
  if (!message || typeof message !== 'object') return '';
  const fromError = extractGenieErrorText(message);
  const fromAttachments =
    extractGenieTextFromAttachments(message.attachments).trim() ||
    attachmentContentToString(message.attachments?.[0]?.content).trim() ||
    (message.content != null ? String(message.content).trim() : '');
  const failed = String(message.status || '').toUpperCase() === 'FAILED';
  if (failed) {
    return pickGenieFailureDisplayText(fromError, fromAttachments) || fromError || fromAttachments;
  }
  if (fromAttachments) return fromAttachments;
  return fromError;
}

/** Format SSE / thrown error payloads (avoids `[object Object]`). */
function formatGenieStreamErrorValue(error) {
  if (error == null) return '';
  if (typeof error === 'string') {
    const s = error.trim();
    if (!s || s === '[object Object]') return '';
    if (s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s);
        const fromObj =
          extractGenieErrorText({ error: parsed }) || extractGenieErrorText(parsed);
        if (fromObj) return fromObj;
      } catch {
        /* not JSON */
      }
    }
    return s;
  }
  if (typeof error === 'object') {
    const fromMessageShape = extractGenieErrorText({ error });
    if (fromMessageShape) return fromMessageShape;
    const direct =
      (typeof error.error === 'string' && error.error.trim()) ||
      (typeof error.message === 'string' && error.message.trim()) ||
      '';
    if (direct) {
      const t = typeof error.type === 'string' ? error.type.trim() : '';
      return t ? `${t}: ${direct}` : direct;
    }
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error);
}

/** Collect `attachment.text.content` from all attachments (Genie text reply). */
function extractGenieTextFromAttachments(attachments) {
  if (!Array.isArray(attachments)) return '';
  const parts = [];
  for (const att of attachments) {
    const raw = att?.text?.content;
    if (raw == null) continue;
    const s = typeof raw === 'string' ? raw : attachmentContentToString(raw);
    if (s.trim()) parts.push(s);
  }
  return parts.join('\n\n');
}

/** First attachment with `suggested_questions.questions` (index may vary). */
function extractSuggestedQuestionsFromAttachments(attachments) {
  if (!Array.isArray(attachments)) return null;
  for (const att of attachments) {
    const q = att?.suggested_questions?.questions;
    if (q && Array.isArray(q) && q.length > 0) return q;
  }
  return null;
}

/** Matches backend ``_STATEMENT_ID_RE`` / SQL statement proxy validation (UUID). */
const GENIE_STATEMENT_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Browser cookie name for cached Databricks SQL statement JSON (`{statement_id}_json`). */
function genieStatementJsonCookieName(statementId) {
  return `${String(statementId).trim()}_json`;
}

const GENIE_STATEMENT_JSON_COOKIE_DAYS = 1;

/** In-flight fetches by statement id (dedupes Strict Mode remounts and duplicate previews). */
const genieSqlStatementFetchById = new Map();

function readGenieSqlStatementPayloadFromCookie(statementId) {
  const sid = statementId != null ? String(statementId).trim() : '';
  if (!sid) return null;
  const raw = getCookie(genieStatementJsonCookieName(sid));
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Load SQL statement JSON from cookie, or fetch once per id (shared across components). */
function fetchGenieSqlStatementPayload(statementId) {
  const sid = statementId != null ? String(statementId).trim() : '';
  if (!sid) return Promise.reject(new Error('statement_id is required'));

  const fromCookie = readGenieSqlStatementPayloadFromCookie(sid);
  if (fromCookie != null) return Promise.resolve(fromCookie);

  const inFlight = genieSqlStatementFetchById.get(sid);
  if (inFlight) return inFlight;

  const promise = api
    .get(`/api/v2/chat/genie/sql-statements/${encodeURIComponent(sid)}`)
    .then((res) => {
      const payload = res.data;
      try {
        setCookie(
          genieStatementJsonCookieName(sid),
          JSON.stringify(payload),
          GENIE_STATEMENT_JSON_COOKIE_DAYS
        );
      } catch {
        // ignore cookie write failure (e.g. size limit)
      }
      return payload;
    })
    .finally(() => {
      genieSqlStatementFetchById.delete(sid);
    });

  genieSqlStatementFetchById.set(sid, promise);
  return promise;
}

/** Split stored ``statement_id`` (comma-separated) into validated, de-duplicated statement ids. */
function parseStatementIdsCsv(raw) {
  if (raw == null || String(raw).trim() === '') return [];
  const seen = new Set();
  const ordered = [];
  for (const part of String(raw).split(',')) {
    const s = part.trim();
    if (GENIE_STATEMENT_ID_RE.test(s) && !seen.has(s)) {
      seen.add(s);
      ordered.push(s);
    }
  }
  return ordered;
}

/**
 * Same collection order as backend ``_collect_statement_ids_from_genie_message``:
 * ``query_result.statement_id`` first, then every ``attachments[].query.statement_id`` in order,
 * de-duplicated. Returns a comma-separated string or null.
 */
function extractStatementIdsCsvFromGenieMessage(msg) {
  if (!msg || typeof msg !== 'object') return null;
  const ordered = [];
  const seen = new Set();
  const add = (sidRaw) => {
    const sid = sidRaw != null ? String(sidRaw).trim() : '';
    if (sid && !seen.has(sid)) {
      seen.add(sid);
      ordered.push(sid);
    }
  };
  const qr = msg.query_result;
  if (qr && typeof qr === 'object' && qr.statement_id != null) add(qr.statement_id);
  const atts = msg.attachments;
  if (Array.isArray(atts)) {
    for (const att of atts) {
      const q = att?.query;
      if (q && typeof q === 'object') {
        if (q.statement_id != null) add(q.statement_id);
        else if (q.statementId != null) add(q.statementId);
      }
    }
  }
  return ordered.length ? ordered.join(',') : null;
}

/** Collect `attachment_id` values from attachments that include a `viz` block. */
function extractVizAttachmentIdsFromGenieMessage(msg) {
  if (!msg || typeof msg !== 'object') return [];
  const atts = msg.attachments;
  if (!Array.isArray(atts)) return [];
  const ordered = [];
  const seen = new Set();
  for (const att of atts) {
    if (!att || typeof att !== 'object' || !att.viz || typeof att.viz !== 'object') continue;
    const aid = att.attachment_id;
    const s = aid != null ? String(aid).trim() : '';
    if (s && !seen.has(s)) {
      seen.add(s);
      ordered.push(s);
    }
  }
  return ordered;
}

function normalizeVizAttachmentIds(raw) {
  if (!Array.isArray(raw)) return [];
  const ordered = [];
  const seen = new Set();
  for (const id of raw) {
    const s = id != null ? String(id).trim() : '';
    if (s && !seen.has(s)) {
      seen.add(s);
      ordered.push(s);
    }
  }
  return ordered;
}

function genieStatementColumnNames(manifest) {
  const cols = manifest?.schema?.columns;
  if (!Array.isArray(cols)) return [];
  return cols.map((c, i) => {
    if (c == null) return `col_${i}`;
    if (typeof c === 'string') return c;
    if (typeof c === 'object') {
      return (
        c.name ??
        c.column_name ??
        c.field_name ??
        (typeof c.title === 'string' ? c.title : null) ??
        `col_${i}`
      );
    }
    return `col_${i}`;
  });
}

/**
 * Normalize Genie/SQL timestamps (e.g. ``2026-02-0100:00:00.0000Z`` missing ``T``) for parsing.
 */
function normalizeGenieTimestampString(raw) {
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}\d{2}:\d{2}/.test(s)) {
    return s.replace(/^(\d{4}-\d{2}-\d{2})(\d{2}:)/, '$1T$2');
  }
  return s;
}

/**
 * Format timestamp category labels for charts.
 * Midnight (``00:00:00`` with optional ``.0000``) → date only; otherwise drop fractional seconds and ``Z``.
 */
function formatGenieChartTimestampLabel(value) {
  if (value == null || value === '') return null;
  const s = normalizeGenieTimestampString(value);
  const m = s.match(
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?:Z|([+-]\d{2}:?\d{2}))?$/i
  );
  if (!m) return null;

  const [, date, hh, mm, ss, fracPart] = m;
  const fracIsZero = !fracPart || /^0+$/.test(fracPart);
  const isMidnight = hh === '00' && mm === '00' && ss === '00' && fracIsZero;
  if (isMidnight) return date;

  return `${date}T${hh}:${mm}:${ss}`;
}

function formatGenieChartCategoryLabel(value) {
  if (value == null || value === '') return value;
  const formatted = formatGenieChartTimestampLabel(value);
  return formatted != null ? formatted : String(value);
}

/** Query results table cell display (formats timestamps consistently). */
function formatGenieQueryResultCell(cell) {
  if (cell == null || cell === '') return '';
  return formatGenieChartCategoryLabel(cell);
}

function GenieSqlStatementPreview({ statementId }) {
  const ids = useMemo(() => parseStatementIdsCsv(statementId), [statementId]);
  if (ids.length === 0) return null;
  return (
    <>
      {ids.map((id, idx) => (
        <div key={id}>
          {ids.length > 1 ? (
            <div
              className={
                idx === 0
                  ? 'mt-3 text-xs font-semibold text-gray-700'
                  : 'mt-4 text-xs font-semibold text-gray-700'
              }
            >
              Result set {idx + 1} of {ids.length}
            </div>
          ) : null}
          <GenieSingleSqlStatementPreview
            statementId={id}
            rootMarginClass={ids.length > 1 ? 'mt-2' : 'mt-3'}
          />
        </div>
      ))}
    </>
  );
}

function GenieSingleSqlStatementPreview({
  statementId,
  rootMarginClass = 'mt-3',
}) {
  const sid = statementId != null ? String(statementId).trim() : '';
  const [loadState, setLoadState] = useState({ status: 'idle', payload: null, error: null });

  useEffect(() => {
    if (!sid) {
      setLoadState({ status: 'idle', payload: null, error: null });
      return undefined;
    }
    const fromCookie = readGenieSqlStatementPayloadFromCookie(sid);
    if (fromCookie != null) {
      setLoadState({ status: 'done', payload: fromCookie, error: null });
      return undefined;
    }

    let cancelled = false;
    setLoadState({ status: 'loading', payload: null, error: null });
    fetchGenieSqlStatementPayload(sid)
      .then((payload) => {
        if (!cancelled) setLoadState({ status: 'done', payload, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          const msg = formatErrorDetail(detail) || err.message || 'Failed to load query results.';
          setLoadState({ status: 'error', payload: null, error: msg });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sid]);

  if (!sid) return null;

  if (loadState.status === 'loading') {
    return (
      <div className={`${rootMarginClass} rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-500`}>
        Loading query results…
      </div>
    );
  }
  if (loadState.status === 'error') {
    return (
      <div className={`${rootMarginClass} rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-900`}>
        {loadState.error}
      </div>
    );
  }
  if (loadState.status !== 'done' || !loadState.payload) return null;

  const payload = loadState.payload;
  const state = payload?.status?.state != null ? String(payload.status.state).toUpperCase() : '';
  if (state === 'FAILED') {
    const em = payload?.status?.error?.message || 'Statement failed.';
    return (
      <div className={`${rootMarginClass} rounded-md border border-red-200 bg-red-50/80 px-3 py-2 text-xs text-red-900`}>
        {em}
      </div>
    );
  }
  const dataArray = payload?.result?.data_array;

  let names = genieStatementColumnNames(payload?.manifest);
  if (Array.isArray(dataArray) && dataArray.length > 0) {
    const firstRow = dataArray[0];
    const n = Array.isArray(firstRow) ? firstRow.length : 0;
    if (names.length < n) {
      const extra = [];
      for (let k = names.length; k < n; k += 1) extra.push(`col_${k}`);
      names = names.concat(extra);
    }
    if (n > 0) {
      const tableWidthClass = n <= 4 ? 'w-auto max-w-full' : 'min-w-full';
      return (
        <div className={`${rootMarginClass} overflow-hidden rounded-md border border-gray-200 bg-white`}>
          <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-800">
            Query results
          </div>
          <div className="max-h-64 overflow-auto">
            <table className={`border-collapse text-left text-xs text-gray-800 ${tableWidthClass}`}>
              <thead className="sticky top-0 z-[1] bg-gray-100 text-gray-700 shadow-sm">
                <tr>
                  {names.slice(0, n).map((name, j) => (
                    <th
                      key={j}
                      className={`whitespace-nowrap border-b border-gray-200 px-2 py-1.5 font-semibold${
                        j === 1 ? ' text-right' : ''
                      }`}
                    >
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataArray.map((row, ri) => (
                  <tr key={ri} className="odd:bg-white even:bg-gray-50/80">
                    {names.slice(0, n).map((_, ci) => {
                      const cell = Array.isArray(row) ? row[ci] : null;
                      const tdClass = [
                        'max-w-[14rem] truncate border-b border-gray-100 px-2 py-1.5',
                        ci === 0 ? 'font-bold' : '',
                        ci === 1 ? 'text-right tabular-nums' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <td key={ci} className={tdClass}>
                          {formatGenieQueryResultCell(cell)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  }

  return (
    <div className={`${rootMarginClass} overflow-hidden rounded-md border border-gray-200 bg-white`}>
      <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700">
        Statement status: {state || 'unknown'}
      </div>
      <pre className="max-h-48 overflow-auto p-2 text-[0.7rem] leading-snug text-gray-700">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  );
}

function genieVisualizationProxyUrl({ space, conversationId, messageId, attachmentId }) {
  const cid = conversationId != null ? String(conversationId).trim() : '';
  const mid = messageId != null ? String(messageId).trim() : '';
  const aid = attachmentId != null ? String(attachmentId).trim() : '';
  const params = new URLSearchParams();
  if (space) params.set('space', space);
  const qs = params.toString();
  return `/api/v2/chat/genie/conversations/${encodeURIComponent(cid)}/messages/${encodeURIComponent(mid)}/attachments/${encodeURIComponent(aid)}/visualization${qs ? `?${qs}` : ''}`;
}

async function fetchGenieVisualizationDataUrl({ space, conversationId, messageId, attachmentId }) {
  const url = genieVisualizationProxyUrl({ space, conversationId, messageId, attachmentId });
  const sessionId =
    typeof localStorage !== 'undefined' ? localStorage.getItem('sessionId') : null;
  const headers = {};
  if (sessionId) headers.Authorization = `Bearer ${sessionId}`;

  const res = await fetch(url, { credentials: 'include', headers });
  redirectToLoginIfUnauthorized(res.status);
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    const contentType = res.headers.get('content-type') || '';
    try {
      if (contentType.includes('application/json')) {
        const parsed = await res.json();
        detail = formatErrorDetail(parsed?.detail) || String(parsed?.detail || detail);
      } else {
        const text = await res.text();
        if (text.trim()) detail = text.trim().slice(0, 240);
      }
    } catch {
      /* ignore parse errors */
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  if (!blob || blob.size === 0) {
    throw new Error('Empty visualization response');
  }

  const pngHeader = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  const isPng =
    pngHeader.length === 8 &&
    pngHeader[0] === 0x89 &&
    pngHeader[1] === 0x50 &&
    pngHeader[2] === 0x4e &&
    pngHeader[3] === 0x47;

  if (!isPng) {
    const contentType = blob.type || res.headers.get('content-type') || '';
    if (contentType.includes('json') || contentType.includes('text')) {
      const text = await blob.text();
      throw new Error(text.slice(0, 240) || 'Invalid visualization response');
    }
    throw new Error('Visualization response is not a PNG image');
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read visualization image'));
    reader.readAsDataURL(blob);
  });
}

function GenieSingleVisualizationImage({
  space,
  conversationId,
  messageId,
  attachmentId,
  rootMarginClass = 'mt-3',
  title,
}) {
  const [status, setStatus] = useState('loading');
  const [dataUrl, setDataUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!attachmentId) {
      setStatus('idle');
      setDataUrl(null);
      setErrorMessage('');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    setDataUrl(null);
    setErrorMessage('');

    fetchGenieVisualizationDataUrl({ space, conversationId, messageId, attachmentId })
      .then((url) => {
        if (!cancelled) {
          setDataUrl(typeof url === 'string' ? url : null);
          setStatus('loaded');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(err.message || 'Could not load visualization image.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [space, conversationId, messageId, attachmentId]);

  if (!attachmentId) return null;

  return (
    <div className={`${rootMarginClass} overflow-hidden rounded-md border border-gray-200 bg-white`}>
      <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-800">
        {title || 'Visualization'}
      </div>
      <div className="p-2">
        {status === 'loading' ? (
          <div className="py-6 text-center text-xs text-gray-500">Loading visualization…</div>
        ) : null}
        {status === 'error' ? (
          <div className="rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-900">
            {errorMessage}
          </div>
        ) : null}
        {status === 'loaded' && dataUrl ? (
          <img
            src={dataUrl}
            alt=""
            className="mx-auto max-h-[21rem] w-3/4 object-contain"
          />
        ) : null}
      </div>
    </div>
  );
}

function GenieVisualizationPreview({
  space,
  conversationId,
  messageId,
  vizAttachmentIds,
  rootMarginClass = 'mt-3',
}) {
  const convId =
    conversationId != null ? String(conversationId).trim() : readConversationIdFromStorage();
  const msgId = messageId != null ? String(messageId).trim() : '';
  const resolvedIds = useMemo(
    () => normalizeVizAttachmentIds(vizAttachmentIds),
    [vizAttachmentIds]
  );

  if (!space || !convId || !msgId || resolvedIds.length === 0) return null;

  return (
    <>
      {resolvedIds.map((attachmentId, idx) => (
        <GenieSingleVisualizationImage
          key={attachmentId}
          space={space}
          conversationId={convId}
          messageId={msgId}
          attachmentId={attachmentId}
          rootMarginClass={idx === 0 ? rootMarginClass : 'mt-2'}
          title={resolvedIds.length > 1 ? `Visualization ${idx + 1}` : 'Visualization'}
        />
      ))}
    </>
  );
}

function suggestedQuestionLabel(q) {
  if (typeof q === 'string') return q;
  if (q != null && typeof q === 'object' && 'question' in q) return String(q.question);
  return JSON.stringify(q);
}

/** Escape `*` and `\\` so the question can sit inside markdown `**...**`. */
function escapeForMarkdownBold(s) {
  return s.replace(/\\/g, '\\\\').replace(/\*/g, '\\*');
}

/**
 * One Q/A turn for stored transcript: bold question (single line), reply markdown below.
 * Pairs are joined with TRANSCRIPT_TURN_DELIMITER (`\\n\\n---\\n\\n`).
 */
function buildTranscriptTurn(question, replyBodyText) {
  const q = escapeForMarkdownBold(question.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim());
  let block = `**${q}**`;
  if (replyBodyText?.trim()) {
    block += `\n\n${replyBodyText.trim()}`;
  }
  return block.trimEnd();
}

function genieReplyMarkdownFromData(data) {
  const detail = genieFailureDetailFromData(data);
  if (detail) return detail;
  return genieReplyBodyTextFromMessage(data?.message);
}

function buildTurnMetaFromGenieResponse(data) {
  const pt = data?.persisted_turn;
  const stmtFromPt = pt?.statement_id != null ? String(pt.statement_id).trim() || null : null;
  const statementId =
    stmtFromPt || extractStatementIdsCsvFromGenieMessage(data?.message) || null;
  const conversationId =
    data?.conversation?.id != null ? String(data.conversation.id).trim() || null : null;
  const assistantMessageId =
    pt?.assistant_message_id != null
      ? String(pt.assistant_message_id).trim() || null
      : data?.message?.id != null
        ? String(data.message.id).trim() || null
        : data?.message?.message_id != null
          ? String(data.message.message_id).trim() || null
          : null;
  const vizAttachmentIds = extractVizAttachmentIdsFromGenieMessage(data?.message);
  if (pt?.genie_question_id != null) {
    return {
      genieQuestionId: Number(pt.genie_question_id),
      assistantMessageId,
      rating: null,
      statementId,
      vizAttachmentIds,
      conversationId,
    };
  }
  return {
    genieQuestionId: null,
    assistantMessageId,
    rating: null,
    statementId,
    vizAttachmentIds,
    conversationId,
  };
}

/** Build transcript + persist Genie session keys and per-turn metadata for reload from history. */
function writeGenieSessionFromQuestions(conversationId, questions) {
  const meta = buildTurnMetaFromQuestions(questions);
  const turns = (questions || []).map((q) => buildTranscriptTurn(q.question, q.answer || ''));
  const merged = turns.join(TRANSCRIPT_TURN_DELIMITER);
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(GENIE_CONVERSATION_STORAGE_KEY, String(conversationId).trim());
      sessionStorage.setItem(TRANSCRIPT_STORAGE_KEY, merged);
      sessionStorage.setItem(TRANSCRIPT_TURNS_META_KEY, JSON.stringify(meta));
    }
  } catch {
    /* quota or private mode */
  }
  return { merged, meta };
}

function splitTranscriptIntoTurns(raw) {
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(TRANSCRIPT_TURN_DELIMITER)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Pretty-print JSON from sessionStorage, or raw string if parse fails. */
function formatRawResponseStorageForDisplay() {
  if (typeof sessionStorage === 'undefined') return null;
  let s;
  try {
    s = sessionStorage.getItem(RAW_RESPONSE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (s == null || !String(s).trim()) return null;
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

/** Tailwind-styled elements for Genie markdown replies (no @tailwindcss/typography). */
const chatMarkdownComponents = {
  p: ({ children, ...props }) => (
    <p className="mb-2 last:mb-0 text-sm leading-relaxed text-gray-800" {...props}>
      {children}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-gray-900" {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em className="italic text-gray-800" {...props}>
      {children}
    </em>
  ),
  ul: ({ children, ...props }) => (
    <ul className="my-2 list-disc list-outside pl-5 text-sm text-gray-800 marker:text-gray-500" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="my-2 list-decimal list-outside pl-5 text-sm text-gray-800 marker:text-gray-500" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="mb-1 leading-relaxed last:mb-0" {...props}>
      {children}
    </li>
  ),
  h1: ({ children, ...props }) => (
    <h1 className="mb-2 mt-3 text-lg font-semibold text-gray-900 first:mt-0" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="mb-2 mt-3 text-base font-semibold text-gray-900 first:mt-0" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="mb-1.5 mt-2 text-sm font-semibold text-gray-900 first:mt-0" {...props}>
      {children}
    </h3>
  ),
  a: ({ children, ...props }) => (
    <a className="font-medium text-primary-600 underline decoration-primary-300 hover:text-primary-700" {...props}>
      {children}
    </a>
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = Boolean(className);
    if (!isBlock) {
      return (
        <code className="rounded bg-gray-200/90 px-1 py-0.5 font-mono text-[0.8125rem] text-gray-900" {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className={`block font-mono text-xs text-gray-900 ${className || ''}`} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre className="mb-2 overflow-x-auto rounded-md border border-gray-200 bg-gray-100 p-3 text-xs" {...props}>
      {children}
    </pre>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="my-2 border-l-4 border-gray-300 pl-3 text-sm italic text-gray-600"
      {...props}
    >
      {children}
    </blockquote>
  ),
  hr: (props) => <hr className="my-3 border-gray-200" {...props} />,
};

function renderSuggestedQuestions(questions) {
  if (!questions || !Array.isArray(questions) || questions.length === 0) return null;
  return (
    <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
      {questions.map((q, i) => {
        const label = suggestedQuestionLabel(q);
        return <li key={i}>{label}</li>;
      })}
    </ul>
  );
}

function Chat() {
  const [input, setInput] = useState('');
  const [sentUserText, setSentUserText] = useState('');
  const [result, setResult] = useState(null);
  const [conversationTranscript, setConversationTranscript] = useState(() => readTranscriptFromStorage());
  const [isSending, setIsSending] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showRawJson, setShowRawJson] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyLoadRowId, setHistoryLoadRowId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [genieSpaces, setGenieSpaces] = useState(() => getGenieSpaces());
  const [selectedSpace, setSelectedSpace] = useState(() => readSelectedSpaceFromStorage());
  const [spacesLoading, setSpacesLoading] = useState(() => getGenieSpaces().length === 0);
  const [turnFeedbackMeta, setTurnFeedbackMeta] = useState(() => readTurnMetaFromStorage());
  const [feedbackSavingTurnIndex, setFeedbackSavingTurnIndex] = useState(null);

  const transcriptScrollRef = useRef(null);
  const genieSendAbortRef = useRef(null);

  const message = result?.message;
  const attachments = message?.attachments;
  const replyBodyText = result
    ? genieFailureDetailFromData(result) || genieReplyBodyTextFromMessage(message)
    : '';
  const isGenieFailed = String(message?.status || '').toUpperCase() === 'FAILED';
  const suggestedQuestions = extractSuggestedQuestionsFromAttachments(attachments);
  const hasSuggested = suggestedQuestions && suggestedQuestions.length > 0;
  const hasReplySection = result && replyBodyText !== '';
  const hasTranscript = conversationTranscript.trim().length > 0;
  const liveStatementId =
    result && !hasTranscript ? extractStatementIdsCsvFromGenieMessage(result.message) : null;
  const liveConversationId =
    result?.conversation?.id != null ? String(result.conversation.id).trim() || null : null;
  const liveAssistantMessageId =
    result?.message?.id != null
      ? String(result.message.id).trim() || null
      : result?.message?.message_id != null
        ? String(result.message.message_id).trim() || null
        : null;
  const liveVizAttachmentIds = result
    ? extractVizAttachmentIdsFromGenieMessage(result.message)
    : [];
  const hasLiveVisualization =
    !hasTranscript &&
    selectedSpace &&
    liveConversationId &&
    liveAssistantMessageId &&
    liveVizAttachmentIds.length > 0;

  useEffect(() => {
    if (!hasTranscript) return undefined;
    const el = transcriptScrollRef.current;
    if (!el) return undefined;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [conversationTranscript, hasTranscript]);

  useEffect(() => {
    const turns = splitTranscriptIntoTurns(conversationTranscript);
    setTurnFeedbackMeta((prev) => {
      if (turns.length === 0) {
        if (prev.length === 0) return prev;
        try {
          sessionStorage.removeItem(TRANSCRIPT_TURNS_META_KEY);
        } catch {
          /* ignore */
        }
        return [];
      }
      if (prev.length === turns.length) return prev;
      if (prev.length > turns.length) {
        const sliced = prev.slice(0, turns.length);
        try {
          sessionStorage.setItem(TRANSCRIPT_TURNS_META_KEY, JSON.stringify(sliced));
        } catch {
          /* ignore */
        }
        return sliced;
      }
      const pad = {
        genieQuestionId: null,
        assistantMessageId: null,
        rating: null,
        statementId: null,
        vizAttachmentIds: [],
        conversationId: null,
      };
      const next = [...prev];
      while (next.length < turns.length) next.push({ ...pad });
      try {
        sessionStorage.setItem(TRANSCRIPT_TURNS_META_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [conversationTranscript]);

  useEffect(() => {
    let cancelled = false;
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        const fastCubeOrgs = organizationsService.filterFastCubeOrganizations(orgs);
        setOrganizations(fastCubeOrgs);
        if (fastCubeOrgs.length > 0) {
          setSelectedOrganization(FASTCUBE_ORG);
          organizationsService.setSelectedOrganization(FASTCUBE_ORG, 'name', fastCubeOrgs);
        } else {
          setSelectedOrganization('');
        }
      } catch (err) {
        console.error('Chat: failed to load organizations', err);
        if (!cancelled) {
          setOrganizations([]);
          setSelectedOrganization('');
        }
      } finally {
        if (!cancelled) setOrganizationsLoading(false);
      }
    };
    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadSpaces = async () => {
      const initial = getGenieSpaces();
      if (initial.length === 0) setSpacesLoading(true);
      try {
        const cfg = await fetchCookieConfig();
        if (cancelled) return;
        const spaces = Array.isArray(cfg?.genie_spaces) ? cfg.genie_spaces : [];
        setGenieSpaces(spaces);
        const saved = readSelectedSpaceFromStorage();
        if (saved && spaces.some((s) => s.key === saved)) {
          setSelectedSpace(saved);
        } else if (spaces.length > 0) {
          setSelectedSpace(spaces[0].key);
          writeSelectedSpaceToStorage(spaces[0].key);
        } else {
          setSelectedSpace('');
        }
      } catch (err) {
        console.error('Chat: failed to load Genie spaces', err);
        if (!cancelled) {
          setGenieSpaces([]);
          setSelectedSpace('');
        }
      } finally {
        if (!cancelled) setSpacesLoading(false);
      }
    };
    loadSpaces();
    return () => {
      cancelled = true;
    };
  }, []);

  const resetGenieConversationUi = () => {
    clearConversationIdStorage();
    clearTranscriptStorage();
    clearRawResponseStorage();
    setConversationTranscript('');
    setTurnFeedbackMeta([]);
    setSentUserText('');
    setResult(null);
    setShowRawJson(false);
  };

  const handleSpaceChange = (key) => {
    if (!key || key === selectedSpace) return;
    setSelectedSpace(key);
    writeSelectedSpaceToStorage(key);
    resetGenieConversationUi();
  };

  const applyGenieChatSuccess = async (text, data, { signal } = {}) => {
    const failed = String(data?.message?.status || '').toUpperCase() === 'FAILED';
    if (failed) {
      const resolved = await resolveGenieFailureDetail(data, {
        space: selectedSpace,
        signal,
      });
      if (resolved) {
        data = { ...data, genie_failure_detail: resolved };
      }
    }

    setResult(data);
    setSentUserText(text);
    setInput('');

    const replyMd = genieReplyMarkdownFromData(data);
    const turn = buildTranscriptTurn(text, replyMd);
    setConversationTranscript((prev) => {
      const p = (prev || '').trim();
      const merged = p ? `${p}${TRANSCRIPT_TURN_DELIMITER}${turn}` : turn;
      try {
        sessionStorage.setItem(TRANSCRIPT_STORAGE_KEY, merged);
      } catch {
        /* quota exceeded or storage disabled */
      }
      return merged;
    });

    setTurnFeedbackMeta((prev) => {
      const next = [...prev, buildTurnMetaFromGenieResponse(data)];
      try {
        sessionStorage.setItem(TRANSCRIPT_TURNS_META_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });

    persistConversationIdFromResponse(data);

    try {
      sessionStorage.setItem(RAW_RESPONSE_STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* quota exceeded or storage disabled */
    }

    const errText = genieReplyMarkdownFromData(data);
    if (failed) {
      if (errText) {
        setErrorMessage(errText);
        setShowErrorDialog(true);
        toast.error('Genie could not answer this question.');
      } else {
        setErrorMessage('Genie could not answer this question.');
        setShowErrorDialog(true);
      }
    } else {
      toast.success('Genie response received');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) {
      setErrorMessage('Please enter a message.');
      setShowErrorDialog(true);
      return;
    }
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization.');
      setShowErrorDialog(true);
      return;
    }
    if (!selectedSpace) {
      setErrorMessage('Please select a space.');
      setShowErrorDialog(true);
      return;
    }

    genieSendAbortRef.current?.abort();
    const abortController = new AbortController();
    genieSendAbortRef.current = abortController;

    setIsSending(true);
    setResult(null);
    try {
      const convId = readConversationIdFromStorage();
      const res = await api.post(
        '/api/v2/chat/genie',
        {
          content: text,
          organization: selectedOrganization,
          space: selectedSpace,
          ...(convId ? { conversation_id: convId } : {}),
        },
        { signal: abortController.signal }
      );
      await applyGenieChatSuccess(text, res.data, { signal: abortController.signal });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Chat / Genie error:', err);
      if (err.response?.status === 404) {
        clearConversationIdStorage();
      }
      const errMsg = formatChatGenieSendError(err);
      setErrorMessage(errMsg);
      setShowErrorDialog(true);
    } finally {
      setIsSending(false);
      if (genieSendAbortRef.current === abortController) genieSendAbortRef.current = null;
    }
  };

  const handleNewChat = () => {
    genieSendAbortRef.current?.abort();
    resetGenieConversationUi();
    toast.success('New conversation — next message will start a fresh thread.');
  };

  const openHistory = async () => {
    setHistoryLoadRowId(null);
    setShowHistory(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryItems([]);
    try {
      const res = await api.get('/api/v2/chat/genie/conversations/history');
      const list = Array.isArray(res.data?.conversations) ? res.data.conversations : [];
      setHistoryItems(list);
    } catch (err) {
      console.error('Chat / Genie history error:', err);
      const detail = err.response?.data?.detail;
      setHistoryError(formatErrorDetail(detail) || err.message || 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setShowHistory(false);
    setHistoryLoadRowId(null);
  };

  const handleHistoryConversationClick = async (row) => {
    const cid = row?.conversation_id != null ? String(row.conversation_id).trim() : '';
    if (!cid) return;
    setHistoryLoadRowId(row.id);
    try {
      const res = await api.get(
        `/api/v2/chat/genie/conversations/${encodeURIComponent(cid)}/questions`
      );
      const questions = Array.isArray(res.data?.questions) ? res.data.questions : [];
      const { merged, meta } = writeGenieSessionFromQuestions(cid, questions);
      setConversationTranscript(merged);
      setTurnFeedbackMeta(meta);
      setResult(null);
      setSentUserText('');
      setShowRawJson(false);
      clearRawResponseStorage();
      const oname = row.organization_name;
      if (oname && organizations.length > 0 && organizations.some((o) => o.name === oname)) {
        setSelectedOrganization(oname);
        organizationsService.setSelectedOrganization(oname, 'name', organizations);
      }
      const sk = row.space_key != null ? String(row.space_key).trim() : '';
      if (sk && genieSpaces.some((s) => s.key === sk)) {
        setSelectedSpace(sk);
        writeSelectedSpaceToStorage(sk);
      }
      closeHistoryModal();
      toast.success('Conversation loaded — you can continue below.');
    } catch (err) {
      console.error('Chat / Genie conversation questions error:', err);
      const detail = err.response?.data?.detail;
      toast.error(formatErrorDetail(detail) || err.message || 'Failed to load conversation.');
    } finally {
      setHistoryLoadRowId(null);
    }
  };

  const rawJsonDisplay = formatRawResponseStorageForDisplay();

  const handleTurnFeedback = async (turnIndex, rating) => {
    const meta = turnFeedbackMeta[turnIndex];
    const qid = meta?.genieQuestionId;
    if (qid == null || !Number.isFinite(Number(qid)) || Number(qid) <= 0) {
      toast.error('Feedback is not available for this turn.');
      return;
    }
    setFeedbackSavingTurnIndex(turnIndex);
    try {
      await api.post(`/api/v2/chat/genie/questions/${encodeURIComponent(String(qid))}/feedback`, {
        rating,
      });
      setTurnFeedbackMeta((prev) => {
        const next = [...prev];
        if (next[turnIndex]) next[turnIndex] = { ...next[turnIndex], rating };
        try {
          sessionStorage.setItem(TRANSCRIPT_TURNS_META_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
      toast.success(rating === 1 ? 'Thanks for the positive feedback.' : 'Thanks for your feedback.');
    } catch (err) {
      console.error('Genie feedback error:', err);
      const detail = err.response?.data?.detail;
      toast.error(formatErrorDetail(detail) || err.message || 'Could not save feedback.');
    } finally {
      setFeedbackSavingTurnIndex(null);
    }
  };

  useEffect(() => {
    if (!showRawJson) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape') setShowRawJson(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [showRawJson]);

  useEffect(() => {
    if (!showHistory) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setShowHistory(false);
        setHistoryLoadRowId(null);
      }
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [showHistory]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <CardTitle className="mb-0 text-xl font-semibold tracking-tight text-gray-900">Ask Genie</CardTitle>
            <div className="flex w-full flex-col gap-3 sm:ml-auto sm:w-auto sm:flex-row sm:items-end">
              <div className="w-full space-y-2 sm:w-64 sm:shrink-0">
                <Label htmlFor="chat-genie-organization">Organization</Label>
                {organizationsLoading ? (
                  <p className="text-sm text-gray-500">Loading organizations…</p>
                ) : organizations.length === 0 ? (
                  <p className="text-sm text-amber-600">
                    FastCube access is required to use Ask Genie. Contact an administrator to be added to FastCube.
                  </p>
                ) : (
                  <FunctionalSelect
                    id="chat-genie-organization"
                    value={organizationsService.getSelectedOptionValue(
                      organizations,
                      selectedOrganization,
                      'name'
                    )}
                    onValueChange={(value) => {
                      const parsed = organizationsService.parseOrganizationOptionValue(value);
                      if (parsed) {
                        setSelectedOrganization(parsed.name);
                        organizationsService.setSelectedOrganization(parsed.name, 'name', [
                          { name: parsed.name, id: parsed.id },
                        ]);
                      } else {
                        setSelectedOrganization(value);
                        organizationsService.setSelectedOrganization(value, 'name', organizations);
                      }
                    }}
                    placeholder="Select organization"
                    disabled={isSending}
                    className="w-full"
                  >
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </FunctionalSelect>
                )}
              </div>
              <div className="w-full space-y-2 sm:w-64 sm:shrink-0">
                <Label htmlFor="chat-genie-space">Space</Label>
                {spacesLoading ? (
                  <p className="text-sm text-gray-500">Loading spaces…</p>
                ) : genieSpaces.length === 0 ? (
                  <p className="text-sm text-amber-600">No Genie spaces configured.</p>
                ) : (
                  <FunctionalSelect
                    id="chat-genie-space"
                    value={selectedSpace}
                    onValueChange={handleSpaceChange}
                    placeholder="Select space"
                    disabled={isSending}
                    className="w-full"
                  >
                    {genieSpaces.map((space) => (
                      <SelectItem key={space.key} value={space.key}>
                        {space.label}
                      </SelectItem>
                    ))}
                  </FunctionalSelect>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          {hasTranscript ? (
            <div
              ref={transcriptScrollRef}
              className="space-y-3 max-h-[min(70vh,720px)] overflow-y-auto pr-1"
            >
              {splitTranscriptIntoTurns(conversationTranscript).map((turnMarkdown, i) => {
                const meta = turnFeedbackMeta[i] || {
                  genieQuestionId: null,
                  assistantMessageId: null,
                  rating: null,
                  statementId: null,
                  vizAttachmentIds: [],
                  conversationId: null,
                };
                const canRate =
                  meta.genieQuestionId != null &&
                  Number.isFinite(Number(meta.genieQuestionId)) &&
                  Number(meta.genieQuestionId) > 0;
                const saving = feedbackSavingTurnIndex === i;
                return (
                  <div
                    key={i}
                    className="rounded-md border border-gray-200 bg-gray-50/80 p-3 shadow-sm"
                  >
                    <div className="text-sm [&_a]:break-words">
                      <ReactMarkdown components={chatMarkdownComponents}>{turnMarkdown}</ReactMarkdown>
                    </div>
                    {meta.statementId ? (
                      <GenieSqlStatementPreview statementId={meta.statementId} />
                    ) : null}
                    {meta.vizAttachmentIds?.length > 0 &&
                    selectedSpace &&
                    (meta.conversationId || readConversationIdFromStorage()) &&
                    meta.assistantMessageId ? (
                      <GenieVisualizationPreview
                        space={selectedSpace}
                        conversationId={meta.conversationId || readConversationIdFromStorage()}
                        messageId={meta.assistantMessageId}
                        vizAttachmentIds={meta.vizAttachmentIds}
                      />
                    ) : null}
                    {canRate ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-200/80 pt-3">
                        <span className="text-sm text-gray-600">Is this useful?</span>
                        <button
                          type="button"
                          className={`rounded-md border p-1.5 text-gray-600 transition hover:bg-white hover:text-gray-900 disabled:opacity-40 ${
                            meta.rating === 1
                              ? 'border-primary-300 bg-primary-100 text-primary-800'
                              : 'border-transparent hover:border-gray-200'
                          }`}
                          onClick={() => handleTurnFeedback(i, 1)}
                          disabled={saving || isSending}
                          aria-label="Thumbs up"
                          aria-pressed={meta.rating === 1}
                        >
                          <ThumbsUp className="h-4 w-4" strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          className={`rounded-md border p-1.5 text-gray-600 transition hover:bg-white hover:text-gray-900 disabled:opacity-40 ${
                            meta.rating === -1
                              ? 'border-red-200 bg-red-50 text-red-800'
                              : 'border-transparent hover:border-gray-200'
                          }`}
                          onClick={() => handleTurnFeedback(i, -1)}
                          disabled={saving || isSending}
                          aria-label="Thumbs down"
                          aria-pressed={meta.rating === -1}
                        >
                          <ThumbsDown className="h-4 w-4" strokeWidth={2} />
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {sentUserText ? (
                <div className="rounded-md border-2 border-primary-500 p-3 text-gray-900 whitespace-pre-wrap">
                  {sentUserText}
                </div>
              ) : null}

              {hasReplySection ? (
                <div
                  className={`space-y-3 rounded-md border p-3 ${
                    isGenieFailed
                      ? 'border-red-200 bg-red-50/80'
                      : 'border-gray-200 bg-gray-50/80'
                  }`}
                >
                  <div>
                    <div
                      className={`text-xs font-medium mb-1 ${
                        isGenieFailed ? 'text-red-800' : 'text-gray-500'
                      }`}
                    >
                      {isGenieFailed ? 'Genie error' : 'Reply'}
                    </div>
                    <div className="text-sm [&_a]:break-words">
                      <ReactMarkdown components={chatMarkdownComponents}>{replyBodyText}</ReactMarkdown>
                    </div>
                  </div>
                  {liveStatementId ? (
                    <GenieSqlStatementPreview statementId={liveStatementId} />
                  ) : null}
                  {hasLiveVisualization ? (
                    <GenieVisualizationPreview
                      space={selectedSpace}
                      conversationId={liveConversationId}
                      messageId={liveAssistantMessageId}
                      vizAttachmentIds={liveVizAttachmentIds}
                    />
                  ) : null}
                </div>
              ) : null}
            </>
          )}

          {hasSuggested && result != null ? (
            <div className="rounded-md border border-primary-200 bg-primary-50/40 p-3 shadow-sm">
              <div className="text-xs font-medium text-primary-800 mb-2">Suggested questions</div>
              {renderSuggestedQuestions(suggestedQuestions)}
            </div>
          ) : null}

          {isSending ? (
            <div
              className="flex items-center gap-3 rounded-md border border-primary-200 bg-primary-50/60 px-4 py-3 shadow-sm"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <span
                className="inline-block h-5 w-5 shrink-0 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"
                aria-hidden
              />
              <p className="text-sm font-medium text-gray-900">Genie is working on your answer…</p>
            </div>
          ) : null}

          <form onSubmit={handleSend} className="space-y-4" aria-busy={isSending}>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              disabled={
                isSending ||
                organizationsLoading ||
                !selectedOrganization ||
                organizations.length === 0 ||
                spacesLoading ||
                !selectedSpace ||
                genieSpaces.length === 0
              }
              rows={6}
              className="min-h-[120px]"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="outline"
                disabled={
                  isSending ||
                  organizationsLoading ||
                  !selectedOrganization ||
                  organizations.length === 0 ||
                  spacesLoading ||
                  !selectedSpace ||
                  genieSpaces.length === 0
                }
              >
                {isSending ? 'Sending…' : 'Send'}
              </Button>
              <Button type="button" variant="outline" onClick={handleNewChat} disabled={isSending}>
                New
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={openHistory}
                disabled={isSending}
              >
                History
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRawJson(true)}
                disabled={isSending}
              >
                Raw
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Modal
        isOpen={showRawJson}
        onClose={() => setShowRawJson(false)}
        className="flex max-h-[90vh] w-full max-w-4xl flex-col"
      >
            <CardHeader className="flex-shrink-0 border-b border-gray-100 pb-3 pr-10">
              <CardTitle className="text-lg text-gray-900">Raw response</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden pt-4">
              {rawJsonDisplay != null ? (
                <pre className="max-h-[min(70vh,600px)] flex-1 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-gray-800 whitespace-pre-wrap sm:text-sm">
                  {rawJsonDisplay}
                </pre>
              ) : (
                <p className="text-sm text-gray-500">
                  No raw response stored yet. Send a message first.
                </p>
              )}
            </CardContent>
      </Modal>

      <Modal
        isOpen={showHistory}
        onClose={closeHistoryModal}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col"
      >
            <CardHeader className="flex-shrink-0 border-b border-gray-100 pb-3 pr-10">
              <CardTitle className="text-lg text-gray-900">Conversation history</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Your 10 most recent Genie conversations.</p>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden pt-4">
              {historyLoading ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : historyError ? (
                <p className="text-sm text-red-600 whitespace-pre-wrap">{historyError}</p>
              ) : historyItems.length === 0 ? (
                <p className="text-sm text-gray-500">No saved conversations yet.</p>
              ) : (
                <ul className="max-h-[min(70vh,520px)] space-y-3 overflow-y-auto pr-1">
                  {historyItems.map((row) => (
                    <li key={row.id} className="list-none">
                      <button
                        type="button"
                        className="w-full text-left rounded-md border border-gray-200 bg-gray-50/80 p-3 text-sm shadow-sm transition hover:bg-primary-50/50 hover:border-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => handleHistoryConversationClick(row)}
                        disabled={isSending || historyLoadRowId != null}
                      >
                        <div className="flex items-baseline justify-between gap-2 text-xs text-gray-500">
                          <span>{formatGenieHistoryDate(row.created_at)}</span>
                          <span className="shrink-0 font-medium text-gray-700 text-right">
                            {row.organization_name || '—'}
                          </span>
                        </div>
                        <p className="mt-2 text-left text-sm text-gray-900 whitespace-pre-wrap break-words">
                          <strong className="font-semibold text-gray-900">
                            {genieSpaceLabelFromKey(genieSpaces, row.space_key)}
                          </strong>
                          <span>: </span>
                          {row.first_question}
                        </p>
                        {historyLoadRowId === row.id ? (
                          <p className="mt-2 text-xs text-primary-600">Loading conversation…</p>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
      </Modal>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
}

export default Chat;
